import { lazy, Suspense } from 'react';
import { useVersionCheck } from './hooks/useVersionCheck';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from 'react-helmet-async';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/cs';
import { ThemeContextProvider } from './theme/ThemeContext';

// Providers
import ToastProvider from "./components/toast/ToastProvider";
import { AuthProvider } from "./context/AuthContext";
import { SetupProvider } from "./context/SetupContext";

// Components
import ProtectedRoute from "./components/ProtectedRoute";
import AdminLayout from "./components/AdminLayout";
import ErrorBoundary from "./components/ErrorBoundary";
import SetupGuard from "./components/SetupGuard";
import DynamicTitle from "./components/DynamicTitle";

// Public Pages
import LandingPage from "./Pages/LandingPage";
import DashboardPage from "./Pages/DashboardPage";
import OdpovedPage from "./Pages/OdpovedPage";
import DokumentacePage from "./Pages/DokumentacePage";
import LoginPage from "./Pages/LoginPage";
import SetPasswordPage from "./Pages/SetPasswordPage";
import ForgotPasswordPage from "./Pages/ForgotPasswordPage";
import NotFoundPage from "./Pages/NotFoundPage";
import SetupWizardPage from "./Pages/SetupWizardPage";

// Admin Pages
import AdminDashboardPage from "./Pages/Admin/AdminDashboardPage";
import VyhlaseniPoplachPage from "./Pages/Admin/VyhlaseniPoplachPage";
import SeznamUdalostiPage from "./Pages/Admin/SeznamUdalostiPage";
import DetailUdalostiPage from "./Pages/Admin/DetailUdalostiPage";
import UsersPage from "./Pages/Admin/UsersPage";
import DetailUserPage from "./Pages/Admin/DetailUserPage";
import StatistikyPage from "./Pages/StatistikyPage";
import StatistikyReportyPage from "./Pages/StatistikyReportyPage";
import HealthPage from "./Pages/Admin/HealthPage";
import OdpojeniPage from "./Pages/Admin/OdpojeniPage";
import AuditLogPage from "./Pages/Admin/AuditLogPage";
import ConfigurationPage from "./Pages/Admin/ConfigurationPage";
import SessionsPage from "./Pages/Admin/SessionsPage";
import HydrantyPage from "./Pages/Admin/HydrantyPage";
import DevicesPage from "./Pages/Admin/DevicesPage";
import SpecializacePage from "./Pages/Admin/SpecializacePage";
import TechnikaPage from "./Pages/Admin/TechnikaPage";
import DetailTechnikaPage from "./Pages/Admin/DetailTechnikaPage";
import InventarPage from "./Pages/Admin/InventarPage";
import InventarDetailPage from "./Pages/Admin/InventarDetailPage";
import InventarSettingsPage from "./Pages/Admin/InventarSettingsPage";
import InventuraPage from "./Pages/Admin/InventuraPage";
import InventuraDetailPage from "./Pages/Admin/InventuraDetailPage";
import InventarKategoriePage from "./Pages/Admin/InventarKategoriePage";
import InventarVelikostTypyPage from "./Pages/Admin/InventarVelikostTypyPage";
import KontrolyPage from "./Pages/Admin/KontrolyPage";
import AkcePage from "./Pages/Admin/AkcePage";
import DetailAkcePage from "./Pages/Admin/DetailAkcePage";
import NastenkaPage from "./Pages/Admin/NastenkaPage";
import IoTObjektyPage from "./Pages/Admin/IoTObjektyPage";
import IoTEvidencePage from "./Pages/Admin/IoTEvidencePage";
import IoTObjektDetailPage from "./Pages/Admin/IoTObjektDetailPage";
import IoTDashboardPage from "./Pages/Admin/IoTDashboardPage";
import IoTSenzorTypyPage from "./Pages/Admin/IoTSenzorTypyPage";
import IoTSettingsPage from "./Pages/Admin/IoTSettingsPage";
import DostupnostPage from "./Pages/Admin/DostupnostPage";
import PohotovostPage from "./Pages/Admin/PohotovostPage";
import SablonyUdalostiPage from "./Pages/Admin/SablonyUdalostiPage";
import ExterniKontaktyPage from "./Pages/Admin/ExterniKontaktyPage";
import DevOdpovediPreviewPage from "./Pages/DevOdpovediPreviewPage";
import MojeVybaveniPage from "./Pages/MojeVybaveniPage";
import HydrantDashboardPage from './Pages/Admin/hydranty/HydrantDashboardPage';
import HydrantMapaPage from './Pages/Admin/hydranty/HydrantMapaPage';
import HydrantSeznamPage from './Pages/Admin/hydranty/HydrantSeznamPage';
import HydrantDetailPage from './Pages/Admin/hydranty/HydrantDetailPage';
import HydrantPlanyPage from './Pages/Admin/hydranty/HydrantPlanyPage';
import HydrantZavadyPage from './Pages/Admin/hydranty/HydrantZavadyPage';
import HydrantPlanDetailPage from './Pages/Admin/hydranty/HydrantPlanDetailPage';
import ChatPage from './Pages/ChatPage';

// Lazy load DevTools only in development
const ReactQueryDevtools = lazy(() =>
    import('@tanstack/react-query-devtools').then((mod) => ({
        default: mod.ReactQueryDevtools,
    }))
);

/**
 * Plovoucí tlačítko React Query Devtools (jen vývojový build) leží vpravo dole přes obsah.
 * Na poplachové obrazovce (kiosek) zakrývalo konec poznámky a počítadla a kazilo měření
 * rozložení, proto se tam nevykresluje. Produkční build devtools nemá vůbec.
 */
const QueryDevtoolsMimoKiosek = () => {
    const { pathname } = useLocation();
    if (process.env.NODE_ENV !== 'development' || pathname.startsWith('/poplach')) return null;
    return (
        <Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
        </Suspense>
    );
};

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            staleTime: 30000,
        },
    },
});

/**
 * Cela routovaci cast stromu (vsechny providery KROME `BrowserRouter`) - HO-147
 * Task 4 ji potrebuje vytazenou zvlast, protoze `App` si nese vlastni
 * `BrowserRouter` (cte skutecnou URL prohlizece) a testy tak nejdou presmerovat
 * na jinou routu. `AppRoutes` se v testech obali `MemoryRouter` misto toho
 * (vzor `src/components/ProtectedRoute.test.tsx`).
 */
export const AppRoutes = () => {
    return (
        <HelmetProvider>
            <ThemeContextProvider>
                <CssBaseline />
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="cs">
                <ErrorBoundary>
                    <ToastProvider>
                            <QueryClientProvider client={queryClient}>
                                <DynamicTitle />
                                <SetupProvider>
                                    <AuthProvider>
                                        <QueryDevtoolsMimoKiosek />
                                        <SetupGuard>
                                            <Routes>
                                                {/* Setup Route */}
                                                <Route path="/setup" element={<SetupWizardPage />} />

                                                {/* Public Routes */}
                                                <Route path="/" element={<LandingPage />} />
                                                <Route path="/poplach" element={<DashboardPage />} />
                                                <Route path="/dev/odpovedi-preview" element={<DevOdpovediPreviewPage />} />
                                                <Route path="/odpoved" element={<OdpovedPage />} />
                                                <Route path="/navod" element={<DokumentacePage />} />
                                                <Route path="/dokumentace" element={<DokumentacePage />} />
                                                <Route path="/login" element={<LoginPage />} />
                                                <Route path="/nastav-heslo" element={<SetPasswordPage />} />
                                                <Route path="/zapomenute-heslo" element={<ForgotPasswordPage />} />


                                                {/* Admin Routes (require authentication) */}
                                                <Route
                                                    path="/admin"
                                                    element={
                                                        <ProtectedRoute>
                                                            <AdminLayout />
                                                        </ProtectedRoute>
                                                    }
                                                >
                                                    <Route index element={<AdminDashboardPage />} />
                                                    {/*
                                                        Chat jednotky (HO-147 Task 4) UVNITR /admin stromu, a to
                                                        BEZ `requireFeature`: vypnuty MODUL_CHAT ma skryt jen
                                                        psaci pole (resi si ChatPage), ne cely modul - historie
                                                        musi zustat dosazitelna. `ProtectedRoute` u rodice
                                                        `/admin` zadnou feature branu nema, ta je opt-in per
                                                        routa (viz `inventar` nize), takze tu nic negatuje.

                                                        Drive stranka bydlela mimo /admin s oduvodnenim, ze by ji
                                                        FeatureGuard shodil. To neplatilo a stalo to cely admin
                                                        shell: po kliknuti na polozku v menu zmizel sidebar,
                                                        drobeckova navigace i spodni lista, a ze stranky nevedla
                                                        zadna cesta zpet krome tlacitka prohlizece. Na vyjezdovem
                                                        tabletu behem zasahu je to nejhorsi mozna chvile ztratit
                                                        navigaci.
                                                    */}
                                                    <Route path="chat" element={<ChatPage />} />
                                                    <Route path="poplach" element={<VyhlaseniPoplachPage />} />
                                                    <Route path="sablony" element={<SablonyUdalostiPage />} />
                                                    <Route path="udalosti" element={<SeznamUdalostiPage />} />
                                                    <Route path="udalosti/:id" element={<DetailUdalostiPage />} />
                                                    <Route path="users" element={<UsersPage />} />
                                                    <Route path="users/:id" element={<DetailUserPage />} />
                                                    <Route path="statistiky" element={<StatistikyReportyPage />} />
                                                    <Route path="reporty" element={<Navigate to="/admin/statistiky" replace />} />
                                                    <Route path="statistiky-old" element={<StatistikyPage />} />
                                                    <Route path="health" element={<HealthPage />} />
                                                    <Route path="upozorneni-odpojeni" element={<ProtectedRoute requireAdmin><OdpojeniPage /></ProtectedRoute>} />
                                                    <Route path="audit" element={<AuditLogPage />} />
                                                    <Route path="configuration" element={<ConfigurationPage />} />
                                                    <Route path="sessions" element={<SessionsPage />} />
                                                    <Route path="hydranty" element={<HydrantyPage />} />
                                                    <Route path="hydranty-evidence" element={<HydrantDashboardPage />} />
                                                    <Route path="hydranty-evidence/mapa" element={<HydrantMapaPage />} />
                                                    <Route path="hydranty-evidence/seznam" element={<HydrantSeznamPage />} />
                                                    <Route path="hydranty-evidence/plany" element={<HydrantPlanyPage />} />
                                                    <Route path="hydranty-evidence/plany/:planId" element={<HydrantPlanDetailPage />} />
                                                    <Route path="hydranty-evidence/zavady" element={<HydrantZavadyPage />} />
                                                    <Route path="hydranty-evidence/:id" element={<HydrantDetailPage />} />
                                                    <Route path="devices" element={<DevicesPage />} />
                                                    <Route path="specializace" element={<SpecializacePage />} />
                                                    <Route path="technika" element={<TechnikaPage />} />
                                                    <Route path="technika/:id" element={<DetailTechnikaPage />} />
                                                    <Route path="inventar" element={<ProtectedRoute requireFeature="MODUL_INVENTAR"><InventarPage /></ProtectedRoute>} />
                                                    <Route path="inventar/nastaveni" element={<ProtectedRoute requireFeature="MODUL_INVENTAR"><InventarSettingsPage /></ProtectedRoute>} />
                                                    <Route path="inventar/kategorie" element={<ProtectedRoute requireFeature="MODUL_INVENTAR"><InventarKategoriePage /></ProtectedRoute>} />
                                                    <Route path="inventar/velikost-typy" element={<ProtectedRoute requireFeature="MODUL_INVENTAR"><InventarVelikostTypyPage /></ProtectedRoute>} />
                                                    <Route path="inventar/:id" element={<ProtectedRoute requireFeature="MODUL_INVENTAR"><InventarDetailPage /></ProtectedRoute>} />
                                                    <Route path="inventura" element={<ProtectedRoute requireFeature="MODUL_INVENTAR"><InventuraPage /></ProtectedRoute>} />
                                                    <Route path="inventura/:id" element={<ProtectedRoute requireFeature="MODUL_INVENTAR"><InventuraDetailPage /></ProtectedRoute>} />
                                                    <Route path="kontroly" element={<KontrolyPage />} />
                                                    <Route path="dostupnost" element={<DostupnostPage />} />
                                                    <Route path="pohotovost" element={<PohotovostPage />} />
                                                    <Route path="akce" element={<ProtectedRoute requireFeature="MODUL_AKCE"><AkcePage /></ProtectedRoute>} />
                                                    <Route path="akce/:id" element={<ProtectedRoute requireFeature="MODUL_AKCE"><DetailAkcePage /></ProtectedRoute>} />
                                                    <Route path="nastenka" element={<ProtectedRoute requireFeature="MODUL_NASTENKA"><NastenkaPage /></ProtectedRoute>} />
                                                    {/*
                                                        IoT: jeden guard pro vsechny IoT routy - MODUL_IOT (spec 2026-10-05-shelly-spinace
                                                        oddil 10) a jen pro spravce (spec IoT UX K12: BE vraci clenovi 403, web ho neodhlasi).
                                                    */}
                                                    <Route element={<ProtectedRoute requireAdmin requireFeature="MODUL_IOT"><Outlet /></ProtectedRoute>}>
                                                        <Route path="iot" element={<IoTDashboardPage />} />
                                                        <Route path="iot/objekty" element={<IoTObjektyPage />} />
                                                        <Route path="iot/objekty/:id" element={<IoTObjektDetailPage />} />
                                                        <Route path="iot/zarizeni" element={<IoTEvidencePage />} />
                                                        <Route path="iot/senzor-typy" element={<IoTSenzorTypyPage />} />
                                                        <Route path="iot/nastaveni" element={<IoTSettingsPage />} />
                                                    </Route>
                                                    <Route path="externi-kontakty" element={<ProtectedRoute requireFeature="MODUL_EXTERNI_KONTAKTY"><ExterniKontaktyPage /></ProtectedRoute>} />
                                                    <Route path="moje-vybaveni" element={<ProtectedRoute requireFeature="MODUL_INVENTAR"><MojeVybaveniPage /></ProtectedRoute>} />
                                                </Route>

                                                {/* Fallback pro neexistující cesty */}
                                                <Route path="*" element={<NotFoundPage />} />
                                            </Routes>
                                        </SetupGuard>
                                    </AuthProvider>
                                </SetupProvider>
                            </QueryClientProvider>
                    </ToastProvider>
                </ErrorBoundary>
            </LocalizationProvider>
            </ThemeContextProvider>
        </HelmetProvider>
    );
};

function App() {
    useVersionCheck();

    return (
        <BrowserRouter basename={"/"}>
            <AppRoutes />
        </BrowserRouter>
    );
}

export default App;
